import React, { useState } from 'react';
import styled from 'styled-components';
import { media, spaces } from '../../lib/styles';
import { NextButton, PrevButton } from './PaginationButton/PaginationButton';
import PaginationSummary from './PaginationSummary';
import PaginationList from './PaginationList';

export interface PaginationProps {
    totalPages: number;
    initialPage?: number;
    featureColor?: string;
    onClick: (page: number) => void;
}

const PaginationNav = styled.nav`
    display: inline-flex;
    justify-content: center;
    align-items: center;
    width: 100%;
`;

const PaginationSummaryWrapper = styled.div`
    display: none;
    padding: 0 ${spaces.space8}px;
    justify-content: center;

    @media ${media('small')} {
        display: inline-flex;
    }
`;

const PaginationListWrapper = styled.div`
    display: inline-block;
    margin: 0 ${spaces.space10}px;
    display: flex;
    flex-grow: 1;
    justify-content: space-around;
    width: min-content;

    @media ${media('small')} {
        display: none;
    }
`;

export default function Pagination({
    totalPages,
    featureColor,
    initialPage = 1,
    onClick,
}: PaginationProps) {
    const [currentPage, setCurrentPage] = useState<number>(initialPage);

    const goToPrevPage = () => {
        if (currentPage != 1) {
            setCurrentPage(currentPage - 1);
            onClick(currentPage - 1);
        }
    };

    const goToNextPage = () => {
        if (currentPage != totalPages) {
            setCurrentPage(currentPage + 1);
            onClick(currentPage + 1);
        }
    };

    const goToPage = (page: number) => {
        setCurrentPage(page);
        onClick(page);
    };

    if (totalPages === 1) {
        return null;
    }

    return (
        <PaginationNav role={'navigation'} aria-label={__('Pagination navigation')}>
            <PrevButton
                color={featureColor}
                onClick={goToPrevPage}
                isDisabled={currentPage === 1}
            />
            <PaginationSummaryWrapper>
                <PaginationSummary currentPage={currentPage} totalPages={totalPages} />
            </PaginationSummaryWrapper>
            <PaginationListWrapper>
                <PaginationList
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onClick={goToPage}
                />
            </PaginationListWrapper>
            <NextButton
                color={featureColor}
                onClick={goToNextPage}
                isDisabled={currentPage === totalPages}
            />
        </PaginationNav>
    );
}
